import React from "react";
import {
    Actions,
    Box,
    Button,
    Divider,
    Drawer,
    Dropdown,
    Stack,
    Text,
} from "braid-design-system";
import NumericTextField from "../NumericTextField";
import { BomIngredientMasterItem, BomIngredientRow, computeIngredientTotalQty } from "../../types/bom";

interface AddEditIngredientDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    ingredientMaster: BomIngredientMasterItem[];
    existingIngredientCodes: string[];
    ingredientToEdit?: BomIngredientRow;
    onSave: (row: BomIngredientRow) => void;
}

const AddEditIngredientDrawer: React.FC<AddEditIngredientDrawerProps> = ({
    isOpen,
    onClose,
    ingredientMaster,
    existingIngredientCodes,
    ingredientToEdit,
    onSave,
}) => {
    const [ingredientCode, setIngredientCode] = React.useState("");
    const [qtyPer1, setQtyPer1] = React.useState<string>("");
    const [wastagePct, setWastagePct] = React.useState<string>("");
    const [error, setError] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        if (isOpen) {
            setIngredientCode(ingredientToEdit?.ingredient_code || "");
            setQtyPer1(ingredientToEdit ? String(ingredientToEdit.qty_per_1) : "");
            setWastagePct(ingredientToEdit ? String(ingredientToEdit.wastage_pct) : "0");
            setError(undefined);
        }
    }, [isOpen, ingredientToEdit]);

    const selectableIngredients = React.useMemo(() => {
        return ingredientMaster.filter(
            (ing) => ing.ingredient_code === ingredientToEdit?.ingredient_code || !existingIngredientCodes.includes(ing.ingredient_code),
        );
    }, [ingredientMaster, existingIngredientCodes, ingredientToEdit]);

    const selectedMaster = React.useMemo(
        () => ingredientMaster.find((ing) => ing.ingredient_code === ingredientCode),
        [ingredientMaster, ingredientCode],
    );

    const previewTotalQty = React.useMemo(() => {
        const qty = parseFloat(qtyPer1);
        const wastage = parseFloat(wastagePct);
        if (Number.isNaN(qty)) return null;
        return computeIngredientTotalQty({ qty_per_1: qty, wastage_pct: Number.isNaN(wastage) ? 0 : wastage });
    }, [qtyPer1, wastagePct]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const qty = parseFloat(qtyPer1);

        if (!ingredientCode) {
            setError("Please select an ingredient.");
            return;
        }
        if (Number.isNaN(qty) || qty <= 0) {
            setError("Qty per 1 must be a positive number.");
            return;
        }
        if (!selectedMaster) {
            setError("Selected ingredient is no longer available.");
            return;
        }

        const wastage = parseFloat(wastagePct);

        onSave({
            row_id: ingredientToEdit?.row_id || `${Date.now()}`,
            ingredient_code: selectedMaster.ingredient_code,
            ingredient_name: selectedMaster.ingredient_name,
            uom: selectedMaster.uom,
            qty_per_1: qty,
            wastage_pct: Number.isNaN(wastage) ? 0 : wastage,
            unit_cost: selectedMaster.unit_cost,
        });
        onClose();
    };

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            title={ingredientToEdit ? "Edit Ingredient" : "Add Ingredient"}
            width="small"
        >
            <form onSubmit={handleSubmit} autoComplete="off">
                <Box padding="large">
                    <Stack space="large">
                        <Dropdown
                            id="bom-ingredient-code"
                            label="Ingredient*"
                            value={ingredientCode}
                            onChange={(e) => setIngredientCode(e.currentTarget.value)}
                        >
                            {[
                                <option key="" value="">Select an ingredient...</option>,
                                ...selectableIngredients.map((ing) => (
                                    <option key={ing.ingredient_code} value={ing.ingredient_code}>
                                        {`${ing.ingredient_code} - ${ing.ingredient_name}`}
                                    </option>
                                )),
                            ]}
                        </Dropdown>

                        <NumericTextField
                            label={`Qty per 1${selectedMaster ? ` (${selectedMaster.uom})` : ""}*`}
                            value={qtyPer1}
                            onChange={setQtyPer1}
                            mode="decimal"
                            decimalPlaces={3}
                        />

                        <NumericTextField
                            label="Wastage %"
                            value={wastagePct}
                            onChange={setWastagePct}
                            mode="decimal"
                            decimalPlaces={2}
                            max={100}
                        />

                        {previewTotalQty != null ? (
                            <Text tone="secondary" size="small">
                                {`Total Qty: ${previewTotalQty.toFixed(3)} ${selectedMaster?.uom || ""}`}
                            </Text>
                        ) : null}

                        {error ? (
                            <Text tone="critical" size="small">
                                {error}
                            </Text>
                        ) : null}

                        <Divider />

                        <Actions>
                            <Button type="submit" tone="brandAccent">
                                {ingredientToEdit ? "Update Ingredient" : "Add Ingredient"}
                            </Button>
                            <Button variant="soft" tone="neutral" onClick={onClose}>
                                Cancel
                            </Button>
                        </Actions>
                    </Stack>
                </Box>
            </form>
        </Drawer>
    );
};

export default AddEditIngredientDrawer;
